import Button from '@/components/buttons/Button/Button'
import DefaultInput from '@/components/inputs/DefaultInput/DefaultInput'
import Textarea from '@/components/inputs/Textarea/Textarea'
import { useTranslation } from 'next-i18next'
import React from 'react'
import Form from '../Form/Form'
import SendIcon from '@/assets/svg/send.svg'
import { usePostContactMutation } from '@/api/contact'
import { Controller, useForm, SubmitHandler } from 'react-hook-form'
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay'
import { Alert } from '@mui/material'

type Props = {
  showLicensePlate?: boolean
}

interface IFormInputs {
  name: string
  email: string,
  phone: string,
  carInfo: string,
  message: string,
}

const ContactForm = (props: Props) => {
  const { t } = useTranslation('common')
  const [postForm] = usePostContactMutation()
  const { control, handleSubmit, reset, formState: { errors } } = useForm<IFormInputs>()
  const [showSuccess, setShowSuccess] = React.useState(false)
  const [showError, setShowError] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  const sendData = (data: IFormInputs) => {
    postForm({
      name: data.name,
      email: data.email,
      phone: data.phone,
      carInfo: data.carInfo,
      message: data.message,
    }).then(() => {
      setShowSuccess(true)
      reset()
    }
    ).catch(() => {
      setShowError(true)
    })
    setLoading(false)
  }

  const onSubmit: SubmitHandler<IFormInputs> = data => {
    setLoading(true)
    setTimeout(() => {
      sendData(data)
    }, 10)
  }

  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    carInfo: '',
    message: '',
  })

  const onChangeHandler = (value: string, name: string): void => setFormData({ ...formData, [name]: value })

  return (
    <div>
      <Form
        onSubmit={handleSubmit(onSubmit)}
      >
        {loading && (
          <LoadingOverlay />
        )}
        <Controller
          name="name"
          control={control}
          rules={{ required: t('validation.required') || '' }}
          render={({ field }) =>
            <DefaultInput
              placeholder={t('contact.name') || ''}
              error={errors.name?.message}
              {...field}
            />
          }
        />
        <Controller
          name="email"
          control={control}
          rules={{
            required: t('validation.required') || '',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: t('validation.email') || '',
            },
          }}
          render={({ field }) =>
            <DefaultInput
              placeholder={t('email') || ''}
              error={errors.email?.message}
              {...field}
            />
          }
        />
        <Controller
          name="phone"
          control={control}
          render={({ field }) =>
            <DefaultInput
              placeholder={t('phone') || ''}
              {...field}
            />
          }
        />
        {props.showLicensePlate && (
          <Controller
            name="carInfo"
            control={control}
            render={({ field }) =>
              <DefaultInput
                placeholder={t('contact.licensePlate') || ''}
                {...field}
              />
            }
          />
        )}
        <Controller
          name="message"
          control={control}
          rules={{ required: t('validation.required') || '' }}
          render={({ field }) =>
            <Textarea
              placeholder={t('message') || ''}
              error={errors.message?.message}
              {...field}
            />
          }
        />
        {showSuccess && (
          <Alert severity="success">{t('contact.success')}</Alert>
        )}
        {showError && (
          <Alert severity="error">{t('error')}</Alert>
        )}
        <div className="flex right">
          <Button
            type={'submit'}
            rounded={true}
            medium={true}
            label={t('send') || ''}
            icon={SendIcon}
            iconPosition={'right'}
          />
        </div>
      </Form>
    </div>
  )
}

export default ContactForm